import { useTranslation } from 'react-i18next'
import { Controller, type ControllerProps, type FieldError, type FieldValues, type FieldPath } from 'react-hook-form'
import type { JSX } from 'react'
import { TextField, type TextFieldProps } from 'settle-ui/components/TextField/TextField'
import { mergeRefs } from 'lib/helpers/mergeRefs/mergeRefs'

type Controller<T extends FieldValues, TName extends FieldPath<T>> = Pick<
  ControllerProps<T, TName>,
  'defaultValue' | 'shouldUnregister' | 'name' | 'rules' | 'control'
>

interface RenderHelperTextParams {
  error: FieldError | undefined
}

export type TextFieldControllerProps<T extends FieldValues, TName extends FieldPath<T>> = TextFieldProps &
  Controller<T, TName> & {
    'data-testid'?: string
    rootRef?: React.Ref<HTMLDivElement>
    renderHelperText?: (params: RenderHelperTextParams) => React.ReactNode
  }

export type TextFieldController = <T extends FieldValues, TName extends FieldPath<T>>(
  props: TextFieldControllerProps<T, TName>,
) => JSX.Element

export const TextFieldController: TextFieldController = ({
  id,
  name,
  rules,
  control,
  variant,
  inputRef,
  defaultValue,
  size = 'medium',
  rootRef,
  shouldUnregister,
  required = true,
  fullWidth = true,
  margin = 'normal',
  color = 'secondary',
  type = 'text',
  renderHelperText,
  ...restTextFieldProps
}) => {
  const { t } = useTranslation()

  return (
    <Controller
      data-testid={id}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      name={name}
      rules={{
        required: { value: required, message: t('validation::cantBeEmpty') },
        ...rules,
      }}
      control={control}
      render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => {
        const getHelperText = () => {
          if (renderHelperText) {
            return renderHelperText({ error })
          }
          return error ? error.message : restTextFieldProps.helperText
        }

        return (
          <TextField
            ref={rootRef}
            data-testid={id}
            {...fieldProps}
            {...restTextFieldProps}
            size={size}
            inputRef={mergeRefs([ref, inputRef])}
            margin={margin}
            variant={variant}
            required={required}
            fullWidth={fullWidth}
            helperText={getHelperText()}
            type={type}
            error={Boolean(error) || restTextFieldProps.error}
          />
        )
      }}
    />
  )
}
